import { Image, Skeleton, Stack } from "@chakra-ui/react";

export const renderAmount = (amount) => {
  if (!amount) {
    amount = 0;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const renderAmountWithType = (amount, type) => {
  if (!amount) {
    amount = 0;
  }

  amount = Math.abs(amount);
  if (type === "debit") {
    amount = amount * -1;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const oppositeType = (type) => {
  if (type === "debit") {
    return "credit";
  } else {
    return "debit";
  }
};

export const isTransactionLinked = (transaction) => {
  if (transaction.journal_entries.length === 0) {
    return false;
  }
  let entriesLinked = transaction.journal_entries.every((e) => e.account);
  return entriesLinked && transaction.vendor;
};

export const isTransactionAdded = (transaction) => {
  if (isTransactionLinked(transaction)) {
    return transaction.journal_entries.every((e) => e.added);
  }
  return false;
};

export const titleCase = (string) => {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
};

export const LoadingTable = () => {
  return (
    <Stack>
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
      <Skeleton height="20px" />
    </Stack>
  );
};

export const customCurrencyFormat = (
  numStr,
  percentage = false,
  wrapNegative = false,
  includeSymbol = true,
  includeZeroDecimal = false,
  zeroAsDash = false,
  explicitSign = false,
  absolute = false
) => {
  let num = parseFloat(numStr);
  if (numStr === "-") return "-";
  if (isNaN(num)) return numStr;

  if (num === 0 && zeroAsDash) return "-";

  const isNegative = num < 0;
  const isPositive = num > 0;

  let absNum = Math.abs(absolute ? num : num).toFixed(2);
  let [integerPart, decimalPart] = absNum.split(".");

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formattedNumber =
    decimalPart !== "00"
      ? `${integerPart}.${decimalPart}`
      : includeZeroDecimal
      ? `${integerPart}.${decimalPart}`
      : integerPart;

  const prefixSign = explicitSign && isPositive ? "+" : "";

  if (percentage) {
    return isNegative && wrapNegative && !absolute
      ? `(${formattedNumber}${includeSymbol ? "%" : ""})`
      : isNegative && !absolute
      ? `-${formattedNumber}${includeSymbol ? "%" : ""}`
      : `${prefixSign}${formattedNumber}${includeSymbol ? "%" : ""}`;
  } else {
    return isNegative && wrapNegative && !absolute
      ? `(${includeSymbol ? "$" : ""}${formattedNumber})`
      : isNegative && !absolute
      ? `-${includeSymbol ? "$" : ""}${formattedNumber}`
      : `${prefixSign}${includeSymbol ? "$" : ""}${formattedNumber}`;
  }
};

export const colorFormat = (numStr, oposite = false) => {
  let num = parseFloat(numStr);
  if (isNaN(num)) return "#458CFF";
  if (num === 0) return "#458CFF";

  const isNegative = num < 0;

  if (oposite) {
    if (isNegative) {
      return "#68BA65";
    } else {
      return "#FF5E5E";
    }
  } else {
    if (isNegative) {
      return "#FF5E5E";
    } else {
      return "#68BA65";
    }
  }
};

export const isGoodValue = (numStr, oposite = false) => {
  const num = parseFloat(numStr);
  if (isNaN(num) || num === 0) return false;

  const isNegative = num < 0;

  return oposite ? isNegative : !isNegative;
};

export const arrowDirection = (numStr, oposite = false) => {
  let num = parseFloat(numStr);
  if (isNaN(num)) return "none";
  if (num === 0) return "none";

  const isNegative = num < 0;

  if (oposite) {
    if (isNegative) {
      return "up";
    } else {
      return "down";
    }
  } else {
    if (isNegative) {
      return "down";
    } else {
      return "up";
    }
  }
};

export const colorExpenseFormat = (
  pl,
  percentage,
  oposite = false,
  profit = false
) => {
  let num = parseFloat(pl);
  let percent = parseFloat(percentage);

  if (isNaN(num) || isNaN(percent)) {
    return profit ? "#458CFF" : "#458CFF";
  }

  let percentageOfPl = percent;

  let percentagePositive = oposite ? num + num * 0.1 : num - num * 0.075;
  let percentageNeutral = num;
  let percentageWarning = oposite ? num - num * 0.2 : num + num * 0.12;
  let percentageCaution = oposite ? num - num * 0.35 : num + num * 0.3;

  if (oposite) {
    if (percentageOfPl > parseFloat(percentagePositive)) {
      return "#68BA65";
    } else if (percentageOfPl >= parseFloat(percentageNeutral)) {
      return "#458CFF";
    } else if (percentageOfPl > parseFloat(percentageWarning)) {
      return "#458CFF";
    } else if (percentageOfPl > parseFloat(percentageCaution)) {
      return "#F1B20E";
    } else {
      return "#FF5E5E";
    }
  } else {
    if (percentageOfPl < parseFloat(percentagePositive)) {
      return "#68BA65";
    } else if (percentageOfPl <= parseFloat(percentageNeutral)) {
      return "#458CFF";
    } else if (percentageOfPl < parseFloat(percentageWarning)) {
      return "#458CFF";
    } else if (percentageOfPl < parseFloat(percentageCaution)) {
      return "#F1B20E";
    } else {
      return "#FF5E5E";
    }
  }
};

export const expenseArrowDirection = (pl, percentage) => {
  let num = parseFloat(pl);
  let percent = parseFloat(percentage);

  if (isNaN(num) || isNaN(percent)) {
    return "none";
  }

  if (percent > num) {
    return "down";
  } else if (percent < num) {
    return "up";
  } else {
    return "none";
  }
};

export const getBackgroundColor = (imageType) => {
  switch (imageType) {
    case "/money-up-arrow.png":
      return "rgba(130, 128, 255, 0.18)";
    case "/money-down-arrow.png":
      return "rgba(255, 94, 94, 0.18)";
    case "/money-bag.png":
      return "rgba(104, 186, 101, 0.18)";
    case "/bar-chart-with-arrow.png":
      return "rgba(104, 186, 101, 0.18)";
    default:
      return "transparent";
  }
};

export const DropDownIcon = () => {
  return (
    <Image
      src={"/arrow-dropdown-icon.png"}
      alt="Drop down icon"
      boxSize="10px"
      objectFit="contain"
    />
  );
};

export const AccessToken = () => {
  const localToken = localStorage.getItem("access_token");
  const sessionToken = sessionStorage.getItem("access_token");

  if (localToken === null) {
    return sessionToken;
  } else {
    return localToken;
  }
};

export const validateImageDimensions = (file, { width, height }) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    const img = new window.Image(); // Use the native Image constructor

    reader.onload = (e) => {
      img.onload = () => {
        if (img.width > width || img.height > height) {
          resolve(false); // Dimensions exceed limits
        } else {
          resolve(true); // Valid dimensions
        }
      };
      img.onerror = () => {
        resolve(false); // Handle invalid images
      };
      img.src = e.target.result; // Set the image source to the file data
    };

    reader.readAsDataURL(file); // Read the file as a Data URL
  });
};

export const isFeatureEnabled = (profile, feature) => {
  if (!profile || !profile.enabled_features) {
    return false;
  }

  const rawFeatures = Array.isArray(profile.enabled_features)
    ? profile.enabled_features
    : String(profile.enabled_features).split(",");

  const normalized = rawFeatures
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);

  return normalized.includes(feature);
};

export const colorTargetFormat = (
  pl,
  percentage,
  profit = false,
  opposite = false
) => {
  let num = parseFloat(pl);
  let percent = parseFloat(percentage);

  if (isNaN(num) || isNaN(percent)) {
    return profit ? "#536DEC" : "black";
  }

  let percentageOfPl = percent;

  let percentagePositive = !opposite ? 105 : 92;
  let percentageNeutral = 100;
  let percentageWarning = !opposite ? 88 : 112;
  let percentageCaution = !opposite ? 70 : 130;

  if (!opposite) {
    if (percentageOfPl > parseFloat(percentagePositive)) {
      return "#68BA65";
    } else if (percentageOfPl >= parseFloat(percentageNeutral)) {
      return "#458CFF";
    } else if (percentageOfPl > parseFloat(percentageWarning)) {
      return "#458CFF";
    } else if (percentageOfPl > parseFloat(percentageCaution)) {
      return "#F1B20E";
    } else {
      return "#FF5E5E";
    }
  } else {
    if (percentageOfPl < parseFloat(percentagePositive)) {
      return "#68BA65";
    } else if (percentageOfPl <= parseFloat(percentageNeutral)) {
      return "#458CFF";
    } else if (percentageOfPl < parseFloat(percentageWarning)) {
      return "#458CFF";
    } else if (percentageOfPl < parseFloat(percentageCaution)) {
      return "#F1B20E";
    } else {
      return "#FF5E5E";
    }
  }
};

// CSV preview helpers
// parseCSVLine: parse a single CSV line into fields, supports quoted fields and escaped quotes
export const parseCSVLine = (line) => {
  const res = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        res.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
  }
  res.push(cur);
  return res;
};

// parseCSV: parse text into up to maxRows lines (including header)
export const parseCSV = (text, maxRows = 10) => {
  if (!text) return [];
  const lines = text.split(/\r\n|\n/).filter((l) => l.trim() !== "");
  const out = [];
  for (let i = 0; i < Math.min(lines.length, maxRows + 1); i++) {
    out.push(parseCSVLine(lines[i]));
  }
  return out;
};

// parseFilePreview: reads a File and returns a Promise resolving to parsed rows (header + rows)
export const MAX_PREVIEW_ROWS = 5;

export const parseFilePreview = (file, maxRows = MAX_PREVIEW_ROWS) => {
  return new Promise((resolve, reject) => {
    if (!file) return resolve([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const rows = parseCSV(text, maxRows);
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (err) => reject(err);
    reader.readAsText(file);
  });
};

export const getPixelCount = (basePixels, text, threshold, deductPerChar) => {
  const str = typeof text === "string" ? text : "";
  const length = str.length;

  if (typeof basePixels === "object") {
    const result = {};
    for (const [key, value] of Object.entries(basePixels)) {
      result[key] = getPixelCount(value, text, threshold, deductPerChar);
    }
    return result;
  }

  if (length <= threshold) return basePixels;

  const over = length - threshold;
  const deduction = over * deductPerChar;

  return `calc(${basePixels} - ${deduction}px)`;
};

export const handleYtdToggle = (next) => {
    try {
      sessionStorage.setItem("ytd", JSON.stringify(next));
    } catch {}

    window.dispatchEvent(new Event("ytd-changed"));

  };

export const handlePerformanceYtdToggle = (next) => {
    try {
      sessionStorage.setItem("performanceYtd", JSON.stringify(next));
    } catch {}

    window.dispatchEvent(new Event("performance-ytd-changed"));
  };
